import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import facade from "../services/qwof/apiFacade";
import { useItemSearch } from "../hooks/useItemSearch";
import Spinner from "./Spinner.jsx";
import AssignmentAmountModal from "./AssignmentAmountModal.jsx";
import QuagganEmoji from "./QuagganEmoji.jsx";
import ItemsGrid from "./ItemsGrid.jsx";
import ItemsSelectedWindow from "./ItemsSelectedWindow.jsx";
import {
  clearLabelFilters,
  createDefaultLabelFilters,
  filterItems,
  labelDefs,
  toggleTriStateLabelFilter,
} from "../utils/itemFilters";

const DEFAULT_LABEL_FILTERS = createDefaultLabelFilters();
const ITEM_PAGE_SIZE_OPTIONS = [60, 120, 240];
const DEFAULT_ITEM_PAGE_SIZE = 120;

const ITEM_TYPES_WITH_AMOUNT_PICKER = new Set(["Container", "CraftingMaterial"]);

function FilterChip({ label, value, onClick }) {
  const palette = value === 1
    ? {
        background: "radial-gradient(circle at 20% 0%, rgba(0, 150, 199, 0.95), rgba(3, 4, 94, 0.95))",
        borderColor: "rgba(0, 180, 216, 0.9)",
      }
    : value === -1
      ? {
          background: "radial-gradient(circle at 20% 0%, rgba(130, 25, 60, 0.9), rgba(55, 8, 25, 0.95))",
          borderColor: "rgba(255, 120, 140, 0.72)",
        }
      : {
          background: "radial-gradient(circle at 20% 0%, rgba(1, 42, 74, 0.95), rgba(0, 18, 25, 0.95))",
          borderColor: "rgba(148, 210, 189, 0.7)",
        };

  return (
    <button
      type="button"
      className="names-btn"
      onClick={onClick}
      style={{
        background: palette.background,
        borderColor: palette.borderColor,
      }}
    >
      {label}
      {value === -1 ? " -" : value === 1 ? " +" : ""}
    </button>
  );
}

export default function ItemSelectWindow({
  visible,
  isLoggedIn = false,
  mode,
  selectionMode = "single",
  participants,
  selectedParticipantId,
  selectedItemId,
  onClose,
  onSelectParticipant,
  onSelectItem,
  onFinishSelection,
}) {
  const [search, setSearch] = useState("");
  const [gw2Loading, setGw2Loading] = useState(false);
  const [gw2Message, setGw2Message] = useState("");
  const [labelFilters, setLabelFilters] = useState(DEFAULT_LABEL_FILTERS);
  const [pendingQuantityItem, setPendingQuantityItem] = useState(null);
  const [hoveredItemId, setHoveredItemId] = useState(null);
  const [basketItems, setBasketItems] = useState([]);
  const [itemPageSize, setItemPageSize] = useState(DEFAULT_ITEM_PAGE_SIZE);
  const [itemPage, setItemPage] = useState(0); // Server pagination is 0-indexed
  const deferredSearch = useDeferredValue(search);
  const isBasketMode = mode === "item" && selectionMode === "basket";

  // Use the search hook for server-side pagination and debouncing
  const {
    items: searchedItems,
    totalCount,
    isLoading: isSearchLoading,
    isFetching: isSearchFetching,
    isError: isSearchError,
    error: searchError,
  } = useItemSearch({
    search: mode === "item" ? deferredSearch : "", // Only search in item mode
    type: "", // Could be enhanced with type filtering
    page: mode === "item" ? itemPage : 0,
    limit: itemPageSize,
  });

  // Reset state when visibility or mode changes
  useEffect(() => {
    if (!visible) return;

    setSearch("");
    setLabelFilters(DEFAULT_LABEL_FILTERS);
    setPendingQuantityItem(null);
    setHoveredItemId(null);
    setBasketItems([]);
    setItemPageSize(DEFAULT_ITEM_PAGE_SIZE);
    setItemPage(0);
    setGw2Message("");
  }, [visible, mode]);

  const normalizedSearch = deferredSearch.trim().toLowerCase();

  const filteredParticipants = useMemo(
    () => participants.filter((participant) =>
      String(participant.displayName || "")
        .toLowerCase()
        .includes(normalizedSearch),
    ),
    [participants, normalizedSearch],
  );

  // For item mode: apply label filters to server results
  const filteredItems = useMemo(() => {
    if (mode !== "item") return [];
    
    // Apply label filters to the server results
    return filterItems(searchedItems, { labelFilters, searchTerm: "" });
  }, [searchedItems, labelFilters, mode]);

  // Pagination info (server-side pagination)
  const totalItemPages = useMemo(() => {
    return Math.max(1, Math.ceil(totalCount / itemPageSize));
  }, [totalCount, itemPageSize]);

  // Reset to first page when search or filters change
  useEffect(() => {
    setItemPage(0);
  }, [normalizedSearch, labelFilters]);

  const handleToggleFilter = (key) => {
    setLabelFilters((previous) => toggleTriStateLabelFilter(previous, key));
  };

  const handleClearFilters = () => {
    setLabelFilters(clearLabelFilters());
  };

  const handleSearchGw2 = async () => {
    if (gw2Loading) return;

    setGw2Loading(true);
    setGw2Message("");

    try {
      const result = await facade.searchGw2Items();
      const incomingItems = Array.isArray(result) ? result : [];
      setGw2Message(
        incomingItems.length
          ? `${incomingItems.length} new items synced from GW2`
          : "Item database already up to date",
      );
    } catch (err) {
      const message = err?.fullError || err?.message || "Unknown error";
      setGw2Message(`Failed to sync GW2 items: ${message}`);
    } finally {
      setGw2Loading(false);
    }
  };

  const addItemToBasket = (item, quantity) => {
    setBasketItems((previous) => {
      const existingIndex = previous.findIndex((entry) => String(entry.item?.id) === String(item?.id));

      if (existingIndex === -1) {
        return [...previous, { item, quantity }];
      }

      return previous.map((entry, index) =>
        index === existingIndex
          ? { ...entry, quantity: entry.quantity + quantity, item: { ...entry.item, ...item } }
          : entry,
      );
    });
  };

  const finalizeItemPick = (item, quantity) => {
    if (isBasketMode) {
      addItemToBasket(item, quantity);
      return;
    }

    onSelectItem(item, quantity);
  };

  const handleItemPick = (item) => {
    const itemType = String(item?.type || "").trim();

    if (ITEM_TYPES_WITH_AMOUNT_PICKER.has(itemType)) {
      setPendingQuantityItem(item);
      return;
    }

    finalizeItemPick(item, 1);
  };

  const handleFinishBasket = () => {
    if (!basketItems.length) return;
    onFinishSelection?.(basketItems);
  };

  const handleRemoveFromBasket = (itemId) => {
    setBasketItems((previous) => previous.filter((entry) => String(entry.item?.id) !== String(itemId)));
  };

  if (!visible) return null;

  return createPortal(
    <>
      <AssignmentAmountModal
        visible={!!pendingQuantityItem}
        itemName={pendingQuantityItem?.name || "this item"}
        maxQuantity={250}
        defaultQuantity={250}
        zIndexBase={1450}
        onCancel={() => setPendingQuantityItem(null)}
        onConfirm={(quantity) => {
          if (!pendingQuantityItem) return;
          finalizeItemPick(pendingQuantityItem, quantity);
          setPendingQuantityItem(null);
        }}
      />
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(2, 10, 30, 0.82)",
          backdropFilter: "blur(6px)",
          zIndex: 1350,
        }}
      />

      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1351,
          pointerEvents: "none",
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(1560px, calc(100vw - 20px))",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            gap: 28,
            pointerEvents: "none",
            transform: isBasketMode ? "translateX(-36px)" : "none",
          }}
        >
          <div
            style={{
              width: "min(1100px, calc(100vw - 40px))",
              maxHeight: "min(86vh, 920px)",
              overflow: "hidden",
              borderRadius: 22,
              background: "linear-gradient(160deg, rgba(10, 35, 70, 0.98) 0%, rgba(3, 16, 42, 0.99) 100%)",
              border: "1px solid rgba(80, 180, 255, 0.38)",
              boxShadow: "0 0 40px rgba(0, 150, 255, 0.18), 0 8px 28px rgba(0, 0, 0, 0.72)",
              color: "#e0f7fa",
              pointerEvents: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "22px 22px 12px",
                borderBottom: "1px solid rgba(144, 224, 239, 0.12)",
              }}
            >
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#40e8ff" }}>
                  {mode === "participant" ? "Select Winner" : "Select Item"}
                </div>
                <p className="login-help-text" style={{ marginTop: 6 }}>
                  {mode === "participant"
                    ? "Choose which participant should receive the result."
                    : isBasketMode
                      ? "Choose multiple items, adjust the basket, then click 'Finish' in the window to the right."
                      : isLoggedIn
                        ? "Search your local QWOF item database or sync all items from GW2."
                        : "Search your local QWOF item database."}
                </p>
              </div>

              <button type="button" className="names-btn secondary" onClick={onClose}>
                Close
              </button>
            </div>

            <div style={{ padding: "18px 22px 12px" }}>
              <input
                type="text"
                className="login-input"
                placeholder={mode === "participant" ? "Search participant" : "Search item name"}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ marginBottom: mode === "item" ? 12 : 0 }}
              />

              {mode === "item" ? (
                <>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                    {labelDefs.map((label) => (
                      <FilterChip
                        key={label.key}
                        label={label.key}
                        value={labelFilters[label.key]}
                        onClick={() => handleToggleFilter(label.key)}
                      />
                    ))}
                    <button type="button" className="names-btn secondary" onClick={handleClearFilters}>
                      Clear
                    </button>
                  </div>

                  {isLoggedIn ? (
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
                      <button
                        type="button"
                        className="names-btn primary"
                        onClick={handleSearchGw2}
                        disabled={gw2Loading}
                      >
                        Sync GW2 Items
                      </button>
                      {gw2Message ? (
                        <span className="login-help-text" style={{ marginTop: 0, color: "#9be7ff" }}>
                          {gw2Message}
                        </span>
                      ) : null}
                    </div>
                  ) : null}

                  {filteredItems.length > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, marginTop: 12 }}>
                      <span className="login-help-text" style={{ marginTop: 0 }}>
                        Showing {itemPage * itemPageSize + 1}-{Math.min((itemPage + 1) * itemPageSize, totalCount)} of {totalCount}
                      </span>
                      <label className="login-help-text" style={{ marginTop: 0, display: "inline-flex", alignItems: "center", gap: 6 }}>
                        Per page
                        <select
                          className="login-input"
                          value={itemPageSize}
                          onChange={(event) => setItemPageSize(Number(event.target.value) || DEFAULT_ITEM_PAGE_SIZE)}
                          style={{
                            width: 92,
                            padding: "6px 8px",
                            marginBottom: 0,
                            color: "#dff8ff",
                            background: "linear-gradient(180deg, rgba(8, 26, 56, 0.95), rgba(4, 16, 38, 0.96))",
                            border: "1px solid rgba(80, 180, 255, 0.42)",
                            boxShadow: "inset 0 0 0 1px rgba(80, 180, 255, 0.12)",
                            colorScheme: "dark",
                          }}
                        >
                          {ITEM_PAGE_SIZE_OPTIONS.map((size) => (
                            <option
                              key={size}
                              value={size}
                              style={{
                                color: "#dff8ff",
                                backgroundColor: "#061a36",
                              }}
                            >
                              {size}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="names-btn secondary"
                        onClick={() => setItemPage((previous) => Math.max(0, previous - 1))}
                        disabled={itemPage <= 0}
                      >
                        Previous
                      </button>
                      <span className="login-help-text" style={{ marginTop: 0 }}>
                        Page {itemPage + 1} / {totalItemPages}
                      </span>
                      <button
                        type="button"
                        className="names-btn secondary"
                        onClick={() => setItemPage((previous) => Math.min(totalItemPages - 1, previous + 1))}
                        disabled={itemPage >= totalItemPages - 1}
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>

            <div
              style={{
                padding: "0 22px 22px",
                overflowY: "auto",
                minHeight: 240,
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(64, 200, 255, 0.82) rgba(2, 10, 26, 0.92)",
              }}
            >
              {isSearchLoading || gw2Loading ? (
                <div style={{ paddingTop: 8 }}>
                  <Spinner size={86} />
                  <p className="login-help-text" style={{ textAlign: "center", marginTop: -6 }}>
                    {isSearchLoading ? "Loading items" : "Syncing GW2 items"}
                  </p>
                </div>
              ) : null}

              {isSearchError && (
                <>
                  <div
                    className="register-error"
                    style={{
                      padding: 10,
                      borderRadius: 10,
                      background: "rgba(120, 20, 20, 0.38)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {searchError?.message || "Failed to load items"}
                  </div>
                  <QuagganEmoji type="failure" />
                </>
              )}

              {mode === "participant" ? (
                filteredParticipants.length > 0 ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                    {filteredParticipants.map((participant) => {
                      const selected = participant.localId === selectedParticipantId;

                      return (
                        <button
                          key={participant.localId}
                          type="button"
                          className="names-btn"
                          onClick={() => onSelectParticipant(participant)}
                          style={{
                            padding: "14px 16px",
                            borderRadius: 14,
                            textAlign: "left",
                            background: selected
                              ? "linear-gradient(180deg, rgba(0, 150, 199, 0.28), rgba(2, 16, 42, 0.94))"
                              : "linear-gradient(180deg, rgba(8, 24, 50, 0.92), rgba(5, 16, 34, 0.88))",
                            borderColor: selected ? "rgba(80, 180, 255, 0.55)" : "rgba(80, 180, 255, 0.24)",
                          }}
                        >
                          <div style={{ fontWeight: 800, color: selected ? "#b9f1ff" : "#eafcff" }}>
                            {participant.displayName || "Unnamed participant"}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div
                    style={{
                      borderRadius: 16,
                      border: "1px dashed rgba(144,224,239,0.35)",
                      background: "rgba(8, 24, 50, 0.55)",
                      padding: 22,
                      textAlign: "center",
                      color: "rgba(224,247,250,0.78)",
                    }}
                  >
                    No participants match the current search.
                  </div>
                )
              ) : filteredItems.length > 0 ? (
                <div style={{ display: "grid", gap: 12 }}>
                  <ItemsGrid
                    items={filteredItems}
                    selectedItemId={isBasketMode ? hoveredItemId : selectedItemId}
                    hoveredItemId={hoveredItemId}
                    renderMode="detailed"
                    detailedCardMaxWidth={252}
                    onItemClick={handleItemPick}
                    onItemMouseEnter={(item) => setHoveredItemId(item?.id)}
                    onItemMouseLeave={() => setHoveredItemId(null)}
                  />
                </div>
              ) : (
                <div
                  style={{
                    borderRadius: 16,
                    border: "1px dashed rgba(144,224,239,0.35)",
                    background: "rgba(8, 24, 50, 0.55)",
                    padding: 22,
                    textAlign: "center",
                    color: "rgba(224,247,250,0.78)",
                  }}
                >
                  {isSearchLoading || isSearchFetching ? "Loading..." : "No items match the current search."}
                </div>
              )}
            </div>
          </div>

          <ItemsSelectedWindow
            visible={isBasketMode}
            items={basketItems}
            onRemoveItem={handleRemoveFromBasket}
            onFinish={handleFinishBasket}
            onClose={onClose}
          />
        </div>
      </div>
    </>,
    document.body,
  );
}